import HTMLEditor from 'components/controls/WysiwygEditor';
import UploadFile from 'components/form/UploadFile';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { PCRModel2, PCRStatus } from 'services/EpdClient';
import PcrService from 'services/PcrService';
import AdminPCRService from 'services/admin/AdminPcrService';
import styled from 'styled-components';
import {
  AdminBox,
  Container,
  EditFull,
  H2,
  InfoText,
  Label,
  LabelBox,
  LargeRadiusInput,
  LargeRadiusTextArea,
  SmallRadiusInput,
} from 'styles/Styles.styled';
import { Option } from 'types/types';
import { formatDate } from 'util/utils';

import { customToolbarPcr } from '../../../components/form/customToolbar';

const EditPcr: React.FunctionComponent = () => {
  const { pcrid } = useParams<{ pcrid: string }>();
  const [selectedPcr, setSelectedPcr] = useState<PCRModel2>();
  const status = Object.keys(PCRStatus);
  const statusOptions = status.map((s: any) => {
    return { value: s, label: PCRStatus[s as keyof typeof PCRStatus] };
  });
  const [productCategoriesOptions, setProductCategoriesOptions] = React.useState<any[]>([]);
  const [cpcrProductCategoriesOptions, setCpcrProductCategoriesOptions] = React.useState<any[]>([]);

  const allDateValues = [
    'versionDate',
    'initiatedDate',
    'startOpenConsultationDate',
    'deadlineOpenConsultationDate',
    'reviewAndApprovalDate',
    'publicationDate',
    'showUpdateInfoUntil',
    'approvalDate',
    'deregistrationDate',
    'validUntil',
  ];

  const pcrEditorStyle = {
    border: '1px solid grey',
    padding: '0 1rem',
    fontSize: '14px',
    borderRadius: '3px',
  };

  const addPcrFile = async (pcrId: string, propertyName: string, file: File) => {
    const fileName = file.name;
    const fileType = file.type;
    const reader = new FileReader();
    reader.onload = async () => {
      const fileBlob = reader.result as ArrayBuffer;
      if (fileBlob) {
        const blob = new Blob([new Uint8Array(fileBlob)], { type: fileType });
        try {
          const result = await PcrService.addPcrFile(pcrId, propertyName, fileName, blob);
          setSelectedPcr(result);
          loadPcr();
        } catch {}
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const loadPcr = async () => {
    const fetchPcr = await AdminPCRService.findPcr(pcrid);
    setSelectedPcr(fetchPcr as any);
  };

  useEffect(() => {
    const loadRootCategories = async () => {
      const fetchCategories = await PcrService.getRootCategories();
      setProductCategoriesOptions(
        fetchCategories.map((s: any) => {
          return { value: s.id, label: s.title };
        })
      );
    };

    const loadCpcrProductCategories = async () => {
      const fetchCategories = await PcrService.getNonRootCategories();
      setCpcrProductCategoriesOptions(
        fetchCategories.map((s: any) => {
          return { value: s.id, label: s.title };
        })
      );
    };

    loadPcr();
    loadRootCategories();
    loadCpcrProductCategories();
  }, []);

  if (!selectedPcr || !status || !productCategoriesOptions) {
    return <InfoText>Loading...</InfoText>;
  }

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];

    if (!file || !selectedPcr) {
      return null;
    }

    onChangePcr && onChangePcr(propertyName, file);
  };

  const onChangePcr = async (propertyName: string, val: any) => {
    if (!selectedPcr || !selectedPcr.id) {
      return;
    }

    if (val instanceof File) {
      addPcrFile(selectedPcr.id, propertyName, val);
    } else {
      updatePcrProperty(selectedPcr.id, propertyName, val);
    }
  };

  const on15804CompliantChange = (checked: boolean) => {
    setSelectedPcr((prev) =>
      prev
        ? {
            ...prev,
            is15804Compliant: checked,
            isISO219302017Available: checked,
            isEN158042012A2Available: checked,
          }
        : prev
    );
    onChangePcr('is15804Compliant', checked);
    onChangePcr('isISO219302017Available', checked);
    onChangePcr('isEN158042012A2Available', checked);
  };

  const onRemoveFile = async (fileId: string) => {
    if (!selectedPcr?.id) {
      return;
    }

    try {
      await PcrService.removePcrFile(selectedPcr.id, fileId);
      loadPcr();
    } catch {}
  };

  const updatePcrProperty = async (pcrId: string, propertyName: string, val: any) => {
    let newValue: any;
    if (Array.isArray(val)) {
      newValue = val.map((s: any) => {
        return s.value;
      });
    } else {
      newValue = allDateValues.includes(propertyName) ? new Date(val) : val;
    }
    try {
      await PcrService.updateProperty(pcrId, propertyName, val === undefined ? null : newValue);
    } catch {}
  };

  return (
    <Container>
      <EditFull>
        <H2>PCR details</H2>
        <AdminBox style={{ backgroundColor: '#FDFDFD' }}>
          <CustomSearch>
            <LabelBox>
              <FixedLabel>Name</FixedLabel>
            </LabelBox>
            <ValueBox>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.name}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.name && onChangePcr('name', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Registration number</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.registrationNumber}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.registrationNumber &&
                  onChangePcr('registrationNumber', e.currentTarget.value)
                }
              />
            </FirstDateValue>
            <SecondDateLabel style={{ justifySelf: 'end', alignSelf: 'center' }}>
              <FixedLabel>Status</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue style={{ width: '100%' }}>
              <Select
                options={statusOptions}
                placeholder={selectedPcr?.status}
                isLoading={!status || status.length === 0}
                isClearable={false}
                onChange={(e: any) => onChangePcr('status', e?.value)}
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Version date</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.versionDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('versionDate', e.currentTarget.value)}
              />
            </FirstDateValue>
            <SecondDateLabel style={{ justifySelf: 'end', alignSelf: 'center' }}>
              <FixedLabel>Version</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue style={{ width: 'calc(100% +32px)' }}>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.version}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.version && onChangePcr('version', e.currentTarget.value)
                }
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Short name</FixedLabel>
            </LabelBox>
            <ValueBox>
              <LargeRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.shortName}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.shortName && onChangePcr('shortName', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>CPC name</FixedLabel>
            </LabelBox>
            <ValueBox>
              <LargeRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.cpcName}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.cpcName && onChangePcr('cpcName', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>PCR review panel</FixedLabel>
            </LabelBox>
            <ValueBox>
              <LargeRadiusTextArea
                style={{ width: '100%', height: '17vh', resize: 'none' }}
                defaultValue={selectedPcr?.pcrReviewPanel}
                disabled
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Chair of the PCR review</FixedLabel>
            </LabelBox>
            <ValueBox>
              <LargeRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.chairOfThePCRReview}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.chairOfThePCRReview &&
                  onChangePcr('chairOfThePCRReview', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Initiated date</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.initiatedDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('initiatedDate', e.currentTarget.value)}
              />
            </FirstDateValue>
            <SecondDateLabel>
              <FixedLabel>Start open consultation date</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.startOpenConsultationDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('startOpenConsultationDate', e.currentTarget.value)
                }
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Deadline open consultation date</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.deadlineOpenConsultationDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('deadlineOpenConsultationDate', e.currentTarget.value)
                }
              />
            </FirstDateValue>
            <SecondDateLabel>
              <FixedLabel>Review and approval date</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.reviewAndApprovalDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('reviewAndApprovalDate', e.currentTarget.value)
                }
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Publication date</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.publicationDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('publicationDate', e.currentTarget.value)}
              />
            </FirstDateValue>
            <SecondDateLabel>
              <FixedLabel>Show update info until</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.showUpdateInfoUntil)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('showUpdateInfoUntil', e.currentTarget.value)
                }
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Approval date</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.approvalDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('approvalDate', e.currentTarget.value)}
              />
            </FirstDateValue>
            <SecondDateLabel>
              <FixedLabel>Deregistration date</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.deregistrationDate)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('deregistrationDate', e.currentTarget.value)}
              />
            </SecondDateValue>
            <LabelBox>
              <FixedLabel>Friendly URL</FixedLabel>
            </LabelBox>
            <FirstDateValue>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                defaultValue={selectedPcr?.friendlyUrl}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.friendlyUrl && onChangePcr('friendlyUrl', e.currentTarget.value)
                }
              />
            </FirstDateValue>
            <SecondDateLabel>
              <FixedLabel>Valid until date</FixedLabel>
            </SecondDateLabel>
            <SecondDateValue>
              <StyledDateInput
                type="date"
                defaultValue={formatDate(selectedPcr?.validUntil)}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => onChangePcr('validUntil', e.currentTarget.value)}
              />
            </SecondDateValue>
            <LabelBox style={{ justifySelf: 'start', alignSelf: 'start', paddingTop: '1rem' }}>
              <FixedLabel>Text</FixedLabel>
            </LabelBox>
            <ValueBox>
              <HTMLEditor
                editorStyle={pcrEditorStyle}
                value={selectedPcr?.text}
                toolbarHidden={false}
                toolbar={customToolbarPcr}
                onEdit={(newValue: string) => onChangePcr('text', newValue)}
              ></HTMLEditor>
            </ValueBox>
            <LabelBox style={{ justifySelf: 'start', alignSelf: 'start', paddingTop: '1rem' }}>
              <FixedLabel>Status Text</FixedLabel>
            </LabelBox>
            <ValueBox>
              <HTMLEditor
                editorStyle={pcrEditorStyle}
                value={selectedPcr?.statusText}
                toolbarHidden={false}
                toolbar={customToolbarPcr}
                onEdit={(newValue: string) => onChangePcr('statusText', newValue)}
              ></HTMLEditor>
            </ValueBox>
            <LabelBox style={{ justifySelf: 'start', alignSelf: 'start', paddingTop: '1rem' }}>
              <FixedLabel>Description</FixedLabel>
            </LabelBox>
            <ValueBox>
              <HTMLEditor
                editorStyle={pcrEditorStyle}
                value={selectedPcr?.description}
                toolbarHidden={false}
                toolbar={customToolbarPcr}
                onEdit={(newValue: string) => onChangePcr('description', newValue)}
              ></HTMLEditor>
            </ValueBox>
            <LabelBox style={{ justifySelf: 'start', alignSelf: 'start', paddingTop: '1rem' }}>
              <FixedLabel>Legal Note</FixedLabel>
            </LabelBox>
            <ValueBox>
              <HTMLEditor
                editorStyle={pcrEditorStyle}
                value={selectedPcr?.legalNote}
                toolbarHidden={false}
                toolbar={customToolbarPcr}
                onEdit={(newValue: string) => onChangePcr('legalNote', newValue)}
              ></HTMLEditor>
            </ValueBox>
            <LabelBox>
              <FixedLabel>Prepared by</FixedLabel>
            </LabelBox>
            <ValueBox>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                type="textarea"
                defaultValue={selectedPcr?.preparedBy}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.preparedBy && onChangePcr('preparedBy', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Contributors</FixedLabel>
            </LabelBox>
            <ValueBox>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                type="textarea"
                defaultValue={selectedPcr?.contributors}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.contributors && onChangePcr('contributors', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Keywords</FixedLabel>
            </LabelBox>
            <ValueBox>
              <SmallRadiusInput
                style={{ boxSizing: 'border-box', width: '100%' }}
                type="textarea"
                defaultValue={selectedPcr?.keywords}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  e.currentTarget.value !== selectedPcr.keywords && onChangePcr('keywords', e.currentTarget.value)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Category</FixedLabel>
            </LabelBox>
            <ValueBox>
              <Select
                options={productCategoriesOptions}
                isMulti={true}
                defaultValue={selectedPcr?.productCategories?.map((category) => {
                  return { value: category.id, label: category.title } as Option;
                })}
                placeholder={''}
                isLoading={!productCategoriesOptions || productCategoriesOptions.length === 0}
                isClearable={false}
                onChange={(e) => onChangePcr('productCategories', e)}
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>(C)PCR Specific Category</FixedLabel>
            </LabelBox>
            <ValueBox>
              <Select
                options={cpcrProductCategoriesOptions}
                isMulti={true}
                defaultValue={selectedPcr?.cpcrProductCategories?.map((category) => {
                  return { value: category.id, label: category.title } as Option;
                })}
                placeholder={''}
                isLoading={!cpcrProductCategoriesOptions || cpcrProductCategoriesOptions.length === 0}
                isClearable={true}
                onChange={(e) => onChangePcr('cpcrProductCategories', e)}
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Enabled for Compiler</FixedLabel>
            </LabelBox>
            <ValueBox style={{ minHeight: 0 }}>
              <input
                type="checkbox"
                defaultChecked={selectedPcr?.isVisibleForCompiler}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('isVisibleForCompiler', e.currentTarget.checked)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Is c-PCR (Complementary PCR)</FixedLabel>
            </LabelBox>
            <ValueBox style={{ minHeight: 0 }}>
              <input
                type="checkbox"
                defaultChecked={selectedPcr?.isComplementary}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangePcr('isComplementary', e.currentTarget.checked)
                }
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>Files</FixedLabel>
            </LabelBox>
            <ValueBox>
              <UploadFile
                accept=".pdf"
                title="Please upload your PCR documents."
                uploadFile={uploadFile}
                disabled={false}
                documents={selectedPcr?.documents}
                onRemoveFile={onRemoveFile}
                field="PCR"
                maxSize={false}
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>EN15804+A2 compliant</FixedLabel>
            </LabelBox>
            <ValueBox style={{ minHeight: 0 }}>
              <input
                type="checkbox"
                checked={selectedPcr?.is15804Compliant ?? false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => on15804CompliantChange(e.currentTarget.checked)}
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>ISO 21930:2017 available</FixedLabel>
            </LabelBox>
            <ValueBox style={{ minHeight: 0 }}>
              <input
                type="checkbox"
                checked={selectedPcr?.isISO219302017Available ?? false}
                disabled={selectedPcr?.is15804Compliant}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedPcr((prev) => (prev ? { ...prev, isISO219302017Available: e.currentTarget.checked } : prev));
                  onChangePcr('isISO219302017Available', e.currentTarget.checked);
                }}
              />
            </ValueBox>
            <LabelBox>
              <FixedLabel>EN 15804:2012+A2 available</FixedLabel>
            </LabelBox>
            <ValueBox style={{ minHeight: 0 }}>
              <input
                type="checkbox"
                checked={selectedPcr?.isEN158042012A2Available ?? false}
                disabled={selectedPcr?.is15804Compliant}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedPcr((prev) => (prev ? { ...prev, isEN158042012A2Available: e.currentTarget.checked } : prev));
                  onChangePcr('isEN158042012A2Available', e.currentTarget.checked);
                }}
              />
            </ValueBox>
          </CustomSearch>
        </AdminBox>
      </EditFull>
    </Container>
  );
};

const FixedLabel = styled(Label)`
  flex: 0 0 200px;
  align-self: start;
  margin: 0.5rem 1rem 0.5rem 0;
  padding-top: 10%;

  > svg {
    margin: 0 0.5rem;
  }

  > div + svg {
    margin: 0;
  }
`;

const StyledDateInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  border: solid 1px gray;
  height: 38px;
  border-radius: 3px;
  padding: 0 1rem;
`;

const CustomSearch = styled.div`
  grid-template-columns: 30% 70%;
  grid-column-gap: 10px;
  grid-row-gap: 5px;
  display: grid;
  grid-template-rows: 3;
  justify-content: start;
  align-items: center;
  ${(props) => props.theme.media.tablet} {
    max-width: 80%;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 30% 20% 5% 30% 20%;
  }
`;

export const ValueBox = styled.div`
  min-height: 38px;
  grid-column-start: 2;
  grid-column-end: 3;
  align-self: center;
  ${(props) => props.theme.media.desktop} {
    grid-column-start: 2;
    grid-column-end: 6;
  }
`;
const FirstDateValue = styled.div`
  grid-column-start: 2;
  grid-column-end: 3;
  align-self: center;
  ${(props) => props.theme.media.desktop} {
    grid-column-start: 2;
    grid-column-end: 3;
  }
`;

const SecondDateLabel = styled.div`
  grid-column-start: 1;
  grid-column-end: 2;
  align-self: center;
  ${(props) => props.theme.media.desktop} {
    grid-column-start: 4;
    grid-column-end: 5;
  }
`;

const SecondDateValue = styled.div`
  grid-column-start: 2;
  grid-column-end: 3;
  align-self: center;
  ${(props) => props.theme.media.desktop} {
    grid-column-start: 5;
    grid-column-end: 6;
  }
`;

export default EditPcr;
